import { z } from "zod/v4";

/**
 * User-entered (custom) portfolio data — for users whose brokerage we don't
 * support, or who want to track positions by hand. Stock shares are valued from
 * live market data, while manual cash/liabilities are explicit user-entered
 * account-level balances. Surfaced as a synthetic "Manual" account by
 * `getUserPortfolio`.
 */

/** Max manual holdings per user (caps abuse / keeps the portfolio read bounded). */
export const MAX_MANUAL_HOLDINGS = 50;
export const MAX_MANUAL_PORTFOLIO_BALANCE = 1_000_000_000;

/** Input for adding/updating a manual holding. */
export const z_ManualHoldingInput = z.object({
  symbol: z.string().min(1).max(20),
  units: z.number().positive(),
  is_crypto: z.boolean().default(false),
});

export type T_ManualHoldingInput = z.infer<typeof z_ManualHoldingInput>;

/** Input for setting manual account-level cash and liabilities. */
export const z_ManualPortfolioBalanceInput = z.object({
  cash_balance: z.number().min(0).max(MAX_MANUAL_PORTFOLIO_BALANCE),
  liability_balance: z.number().min(0).max(MAX_MANUAL_PORTFOLIO_BALANCE),
});

export type T_ManualPortfolioBalanceInput = z.infer<
  typeof z_ManualPortfolioBalanceInput
>;

/** Bulk snapshot save used by the AI holdings-import tool. */
export const z_SaveManualHoldingsSnapshotInput = z
  .object({
    holdings: z
      .array(z_ManualHoldingInput)
      .min(1)
      .max(MAX_MANUAL_HOLDINGS)
      .describe(
        "Positions to save. units is the total current shares/units for that symbol, not an increment.",
      ),
    mode: z
      .enum(["merge", "replace"])
      .describe(
        "merge: upsert these symbols, keep other existing manual holdings. replace: these become the entire manual holdings list.",
      ),
  })
  .superRefine((input, context) => {
    const seenSymbols = new Set<string>();
    for (const holding of input.holdings) {
      const symbol = holding.symbol.trim().toUpperCase();
      if (seenSymbols.has(symbol)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate manual holding: ${symbol}`,
          path: ["holdings"],
        });
      }
      seenSymbols.add(symbol);
    }
  });

export type T_SaveManualHoldingsSnapshotInput = z.infer<
  typeof z_SaveManualHoldingsSnapshotInput
>;

/** Canonical all-at-once draft save used by portfolio editors. */
export const z_ManualPortfolioDraftInput = z
  .object({
    holdings: z.array(z_ManualHoldingInput).max(MAX_MANUAL_HOLDINGS),
    cash_balance: z_ManualPortfolioBalanceInput.shape.cash_balance,
    liability_balance: z_ManualPortfolioBalanceInput.shape.liability_balance,
  })
  .superRefine((draft, context) => {
    const seenSymbols = new Set<string>();
    for (const holding of draft.holdings) {
      const symbol = holding.symbol.trim().toUpperCase();
      if (seenSymbols.has(symbol)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate manual holding: ${symbol}`,
          path: ["holdings"],
        });
      }
      seenSymbols.add(symbol);
    }
  });

export type T_ManualPortfolioDraftInput = z.infer<
  typeof z_ManualPortfolioDraftInput
>;

/** A stored manual holding row. */
export type T_ManualHolding = {
  cuid: string;
  symbol: string;
  units: number;
  is_crypto: boolean;
  created_at: Date;
  updated_at: Date;
};

/** Stored manual account-level cash/liability balances. */
export type T_ManualPortfolioBalance = {
  cuid: string | null;
  cash_balance: number;
  liability_balance: number;
  created_at: Date | null;
  updated_at: Date | null;
};
